/**
 * Auth Service — BFF Pattern
 * Session managed via HttpOnly cookies. No tokens in JS.
 */

const API_BASE = '/auth'

export const authService = {
  async getSession() {
    const response = await fetch(`${API_BASE}/session`, {
      credentials: 'include'
    })
    if (!response.ok) throw new Error('Not authenticated')
    return response.json()
  },

  async switchTenant(tenantId) {
    const response = await fetch(`${API_BASE}/switch-tenant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: tenantId }),
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      // FastAPI returns detail as a string for HTTPException, but as an array
      // for Pydantic validation errors (422). Normalize to a readable string.
      let detail = error.detail
      if (Array.isArray(detail)) {
        detail = detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ')
      }
      const err = new Error(typeof detail === 'string' ? detail : 'Failed to switch tenant')
      err.detail = error.detail
      err.status = response.status
      throw err
    }
    return response.json()
  },

  async logout() {
    const response = await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      credentials: 'include'
    })
    if (!response.ok) throw new Error('Logout failed')
    return response.json()
  },

  async getProfile() {
    const response = await fetch(`${API_BASE}/profile`, { credentials: 'include' })
    if (!response.ok) throw new Error('Failed to load profile')
    return response.json()
  },

  async updateProfile(data) {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to update profile')
    }
    return response.json()
  },

  async getAccount() {
    const response = await fetch(`${API_BASE}/account`, { credentials: 'include' })
    if (!response.ok) throw new Error('Failed to load account')
    return response.json()
  },

  async updateAccount(data) {
    const response = await fetch(`${API_BASE}/account`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to update account')
    }
    return response.json()
  },

  async changePassword(currentPassword, newPassword) {
    const response = await fetch(`${API_BASE}/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to change password')
    }
    return response.json()
  },

  async getDemoUsers() {
    const response = await fetch(`${API_BASE}/demo-users`, { credentials: 'include' })
    if (!response.ok) throw new Error('Failed to fetch demo users')
    return response.json()
  },

  async impersonate(userId) {
    const response = await fetch(`${API_BASE}/impersonate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to impersonate')
    }
    return response.json()
  },

  async stopImpersonating() {
    const response = await fetch(`${API_BASE}/stop-impersonate`, {
      method: 'POST',
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to stop impersonating')
    }
    return response.json()
  },

  /**
   * Rotate the cookie-borne refresh token via POST /api/v1/auth/refresh.
   *
   * On success: returns ``{ ok: true, expires_at: string }``.
   *
   * On failure: throws an Error decorated with:
   * - ``error.status`` — the HTTP status (typically 401).
   * - ``error.errorCode`` — one of ``token_reuse_detected``,
   *   ``refresh_expired``, ``refresh_invalid`` (discriminated
   *   contract). May be ``undefined`` if the backend predates the
   *   contract — callers MUST handle the legacy fallback.
   *
   * @returns {Promise<{ ok: boolean, expires_at: string }>}
   */
  async refresh() {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include'
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      // the structured 401 response shape is
      // ``{ error_code, message }``. Legacy responses (or other
      // 401 sources, e.g. proxies) carry a ``detail`` field.
      const message = body.message || body.detail || 'Refresh failed'
      const err = new Error(message)
      err.status = response.status
      err.errorCode = body.error_code
      throw err
    }
    return response.json()
  }
}
